import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFieldMeta } from '../config/evseFields';
import TimeSelect from './TimeSelect';
import { findUnresolvedConflicts, findCoverageGaps } from '../utils/scheduleConflict';

const API = 'https://api.cpms-srlkevcharge.com';

const DAYS = [
  { key: 'Mon', label: 'Mon' },
  { key: 'Tue', label: 'Tue' },
  { key: 'Wed', label: 'Wed' },
  { key: 'Thu', label: 'Thu' },
  { key: 'Fri', label: 'Fri' },
  { key: 'Sat', label: 'Sat' },
  { key: 'Sun', label: 'Sun' },
];

const IconChevron = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const IconCheck = () => (
  <svg width="15" height="15" fill="none" stroke="var(--srlk-green)" strokeWidth="3" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
);
const IconAlert = () => (
  <svg width="15" height="15" fill="none" stroke="var(--red)" strokeWidth="2.5" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const IconTrash = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

function DayPicker({ value, onChange }) {
  const selectedDays = value ? value.split(',').map(d => d.trim()) : [];

  const toggleDay = (dayKey) => {
    const isSelected = selectedDays.includes(dayKey);
    const updated = isSelected
      ? selectedDays.filter(d => d !== dayKey)
      : [...selectedDays, dayKey];
    onChange(updated.length > 0 ? updated.join(',') : '');
  };

  return (
    <div className="day-picker">
      {DAYS.map(day => (
        <button
          type="button"
          key={day.key}
          className={`day-pill ${selectedDays.includes(day.key) ? 'selected' : ''}`}
          onClick={() => toggleDay(day.key)}
          title={day.key}
        >
          {day.label}
        </button>
      ))}
    </div>
  );
}

function EditableField({ level, fieldName, value, isError, onChange }) {
  const meta = getFieldMeta(level, fieldName);
  return (
    <div className="bulk-field">
      <label>{meta.label}</label>
      {meta.type === 'select' ? (
        <select
          className={isError ? 'field-error' : ''}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select...</option>
          {meta.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : (
        <input
          type="text"
          className={isError ? 'field-error' : ''}
          value={value || ''}
          placeholder={meta.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

function TariffScheduleEditor({ match, onUpdate, isInConflict }) {
  const [showDays, setShowDays] = useState(!!match.days_of_week);
  const [showDates, setShowDates] = useState(!!(match.effective_start_date || match.effective_end_date));

  return (
    <div className={`bulk-tariff-match-block ${isInConflict ? 'has-conflict' : ''}`}>
      <div className="bulk-tariff-match-row">
        <div className={`bulk-tariff-match-name ${match.found ? '' : 'not-found'}`}>
          {match.tariff_name}
          {!match.found && <span className="bulk-tariff-not-found-tag">Not found</span>}
        </div>
        {match.found ? (
          <>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Start Time</label>
              <TimeSelect
                value={match.start_time || ''}
                onChange={(val) => onUpdate('start_time', val)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>End Time</label>
              <TimeSelect
                value={match.end_time || ''}
                onChange={(val) => onUpdate('end_time', val)}
              />
            </div>
          </>
        ) : (
          <div style={{ gridColumn: 'span 2', fontSize: 12, color: 'var(--text-muted)' }}>
            This tariff name does not match any existing tariff and will be skipped.
          </div>
        )}
      </div>

      {match.found && (
        <>
          <div className="advanced-toggle-row" style={{ marginTop: 10, paddingTop: 10 }}>
            <label className="advanced-toggle">
              <input type="checkbox" checked={showDays} onChange={() => setShowDays(p => !p)} />
              Specific days
            </label>
            <label className="advanced-toggle">
              <input type="checkbox" checked={showDates} onChange={() => setShowDates(p => !p)} />
              Specific date range
            </label>
          </div>

          {showDays && (
            <div className="advanced-section">
              <DayPicker
                value={match.days_of_week}
                onChange={(val) => onUpdate('days_of_week', val)}
              />
            </div>
          )}

          {showDates && (
            <div className="advanced-section">
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={match.effective_start_date || ''}
                    onChange={(e) => onUpdate('effective_start_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={match.effective_end_date || ''}
                    onChange={(e) => onUpdate('effective_end_date', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function BulkUploadReview({ locations, onUpdateLocation, onRemoveLocation }) {
  const [expanded, setExpanded] = useState(
    Object.fromEntries(locations.map((l, i) => [i, i === 0]))
  );
  const [allTariffs, setAllTariffs] = useState([]);
  const [tariffLookup, setTariffLookup] = useState({});
  const [addingTariffFor, setAddingTariffFor] = useState(null);
  const [newTariffSelection, setNewTariffSelection] = useState({
    tariff_id: '', start_time: '', end_time: '', days_of_week: '', effective_start_date: '', effective_end_date: ''
  });
  const [newShowDays, setNewShowDays] = useState(false);
  const [newShowDates, setNewShowDates] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tariffs/`).then(res => {
      setAllTariffs(res.data);
      const lookup = {};
      res.data.forEach(t => { lookup[t.id] = t.name; });
      setTariffLookup(lookup);
    }).catch(() => {});
  }, []);

  const toggle = (index) => setExpanded(prev => ({ ...prev, [index]: !prev[index] }));

  const updateLocationField = (locIndex, fieldName, value) => {
    const updated = { ...locations[locIndex] };
    updated.location_fields = { ...updated.location_fields, [fieldName]: value || null };
    onUpdateLocation(locIndex, updated);
  };

  const updateOrgName = (locIndex, value) => {
    const updated = { ...locations[locIndex], organisation_name: value };
    onUpdateLocation(locIndex, updated);
  };

  const updateChargerField = (locIndex, chargerIndex, fieldName, value) => {
    const updated = { ...locations[locIndex] };
    updated.chargers = updated.chargers.map((c, i) =>
      i === chargerIndex
        ? { ...c, charger_fields: { ...c.charger_fields, [fieldName]: value || null } }
        : c
    );
    onUpdateLocation(locIndex, updated);
  };

  const updateConnectorField = (locIndex, chargerIndex, connectorIndex, fieldName, value) => {
    const updated = { ...locations[locIndex] };
    updated.chargers = updated.chargers.map((c, ci) => {
      if (ci !== chargerIndex) return c;
      const newConnectors = c.connectors.map((conn, coni) => {
        if (coni !== connectorIndex) return conn;
        const newFields = { ...conn.connector_fields, [fieldName]: value || null };
        const errors = [];
        if (!newFields.connector_type?.trim()) errors.push('Connector Type is missing');
        if (!newFields.max_ampere?.trim()) errors.push('Max Ampere is missing');
        return { ...conn, connector_fields: newFields, valid: errors.length === 0, errors };
      });
      const validCount = newConnectors.filter(c => c.valid).length;
      return { ...c, connectors: newConnectors, valid_connector_count: validCount };
    });
    onUpdateLocation(locIndex, updated);
  };

  const updateTariffMatchField = (locIndex, matchTempId, field, value) => {
    const updated = { ...locations[locIndex] };
    updated.tariff_matches = updated.tariff_matches.map(m =>
      m.tempId === matchTempId ? { ...m, [field]: value } : m
    );
    onUpdateLocation(locIndex, updated);
  };

  const removeTariffMatch = (locIndex, matchTempId) => {
    const updated = { ...locations[locIndex] };
    updated.tariff_matches = updated.tariff_matches.filter(m => m.tempId !== matchTempId);
    onUpdateLocation(locIndex, updated);
  };

  const resolveConflict = (locIndex, tempIdWinner) => {
    const updated = { ...locations[locIndex] };
    updated.tariff_matches = updated.tariff_matches.map(m =>
      m.tempId === tempIdWinner ? { ...m, priority: (m.priority || 0) + 1 } : m
    );
    onUpdateLocation(locIndex, updated);
  };

  const addTariffMatch = (locIndex) => {
    if (!newTariffSelection.tariff_id) return;
    const tariff = allTariffs.find(t => String(t.id) === String(newTariffSelection.tariff_id));
    if (!tariff) return;

    const resetSelection = () => {
      setNewTariffSelection({ tariff_id: '', start_time: '', end_time: '', days_of_week: '', effective_start_date: '', effective_end_date: '' });
      setNewShowDays(false);
      setNewShowDates(false);
    };

    const updated = { ...locations[locIndex] };
    const existingNames = (updated.tariff_matches || []).map(m => m.tariff_name);
    if (existingNames.includes(tariff.name)) {
      setAddingTariffFor(null);
      resetSelection();
      return;
    }

    updated.tariff_matches = [
      ...(updated.tariff_matches || []),
      {
        tempId: `manual-${Date.now()}`,
        tariff_id: tariff.id,
        tariff_name: tariff.name,
        found: true,
        start_time: newTariffSelection.start_time || null,
        end_time: newTariffSelection.end_time || null,
        days_of_week: newTariffSelection.days_of_week || null,
        effective_start_date: newTariffSelection.effective_start_date || null,
        effective_end_date: newTariffSelection.effective_end_date || null,
        priority: 0,
      }
    ];
    onUpdateLocation(locIndex, updated);
    setAddingTariffFor(null);
    resetSelection();
  };

  const LOCATION_FIELD_NAMES = ['station_name', 'address', 'city', 'country', 'zipcode',
    'latitude', 'longitude', 'network_type', 'parking_type', 'free_charging', 'amenities', 'description'];
  const CHARGER_FIELD_NAMES = ['ocpp_id', 'oem_name', 'ocpp_version', 'plug_and_charge', 'qr_code_id'];
  const CONNECTOR_FIELD_NAMES = ['connector_type', 'max_ampere', 'max_voltage', 'charging_speed', 'energy_capacity', 'power_type'];

  return (
    <>
      {locations.map((loc, locIndex) => {
        const isOpen = !!expanded[locIndex];
        const totalConnectors = loc.chargers.reduce((sum, c) => sum + c.connectors.length, 0);
        const validConnectors = loc.chargers.reduce((sum, c) => sum + c.valid_connector_count, 0);
        const isValid = loc.errors.length === 0 && validConnectors === totalConnectors && totalConnectors > 0;
        const foundMatches = (loc.tariff_matches || []).filter(m => m.found);

        const unresolvedConflicts = foundMatches.length >= 2
          ? findUnresolvedConflicts(foundMatches, tariffLookup).map(c => ({
              ...c,
              tempIdA: foundMatches[c.indexA].tempId,
              tempIdB: foundMatches[c.indexB].tempId,
            }))
          : [];
        const gapWarnings = foundMatches.length > 0 ? findCoverageGaps(foundMatches) : [];
        const isMatchInConflict = (tempId) => unresolvedConflicts.some(c => c.tempIdA === tempId || c.tempIdB === tempId);

        return (
          <div key={locIndex} className={`bulk-location-card ${isValid ? 'valid' : 'invalid'}`}>
            <div className="bulk-location-header" onClick={() => toggle(locIndex)}>
              <div>
                <div className="bulk-location-title">
                  {isValid ? <IconCheck /> : <IconAlert />}
                  {loc.location_fields.station_name || loc.station_reference}
                </div>
                <div className="bulk-location-meta">
                  {loc.station_reference} · {loc.chargers.length} charger{loc.chargers.length !== 1 ? 's' : ''} ·{' '}
                  {validConnectors}/{totalConnectors} connector{totalConnectors !== 1 ? 's' : ''} ready
                  {loc.tariff_matches && loc.tariff_matches.length > 0 && (
                    <> · {foundMatches.length}/{loc.tariff_matches.length} tariff{loc.tariff_matches.length !== 1 ? 's' : ''} matched</>
                  )}
                </div>
              </div>
              <IconChevron style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </div>

            {isOpen && (
              <div className="bulk-location-body">
                {loc.errors.length > 0 && (
                  <div className="bulk-errors-inline">
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {loc.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </div>
                )}

                <div className="bulk-tariff-block">
                  <div className="bulk-tariff-block-title">
                    Tariffs — set hours, days, or dates for each, or leave blank for all times
                  </div>

                  {unresolvedConflicts.map((conflict, i) => (
                    <div className="collision-choice-box" key={i}>
                      <div className="collision-choice-message">{conflict.message}</div>
                      <div className="collision-choice-buttons">
                        <button
                          type="button"
                          className="collision-choice-btn"
                          onClick={() => resolveConflict(locIndex, conflict.tempIdA)}
                        >
                          Give priority to {tariffLookup[foundMatches.find(m => m.tempId === conflict.tempIdA)?.tariff_id]}
                        </button>
                        <button
                          type="button"
                          className="collision-choice-btn"
                          onClick={() => resolveConflict(locIndex, conflict.tempIdB)}
                        >
                          Give priority to {tariffLookup[foundMatches.find(m => m.tempId === conflict.tempIdB)?.tariff_id]}
                        </button>
                      </div>
                    </div>
                  ))}

                  {gapWarnings.length > 0 && unresolvedConflicts.length === 0 && (
                    <div className="schedule-gap-banner">
                      <div className="schedule-gap-banner-title">⚠ Some periods have no tariff assigned</div>
                      <ul className="schedule-gap-banner-list">
                        {gapWarnings.map((gap, i) => <li key={i}>{gap}</li>)}
                      </ul>
                    </div>
                  )}

                  {(!loc.tariff_matches || loc.tariff_matches.length === 0) && addingTariffFor !== locIndex && (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                      No tariffs added for this station yet.
                    </div>
                  )}

                  {(loc.tariff_matches || []).map(match => (
                    <div style={{ position: 'relative' }} key={match.tempId}>
                      <TariffScheduleEditor
                        match={match}
                        isInConflict={isMatchInConflict(match.tempId)}
                        onUpdate={(field, value) => updateTariffMatchField(locIndex, match.tempId, field, value)}
                      />
                      <button
                        type="button"
                        className="tariff-schedule-remove"
                        style={{ position: 'absolute', top: 12, right: 12 }}
                        onClick={() => removeTariffMatch(locIndex, match.tempId)}
                        title="Remove this tariff"
                      >
                        <IconTrash />
                      </button>
                    </div>
                  ))}

                  {addingTariffFor === locIndex ? (
                    <div className="tariff-schedule-form-row" style={{ marginTop: 10, marginBottom: 0, flexWrap: 'wrap' }}>
                      <div className="form-group">
                        <label>Select Tariff</label>
                        <select
                          value={newTariffSelection.tariff_id}
                          onChange={(e) => setNewTariffSelection(prev => ({ ...prev, tariff_id: e.target.value }))}
                        >
                          <option value="">Select...</option>
                          {allTariffs.map(t => (
                            <option key={t.id} value={t.id}>{t.name} — {t.organisation_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Start Time</label>
                        <TimeSelect
                          value={newTariffSelection.start_time}
                          onChange={(val) => setNewTariffSelection(prev => ({ ...prev, start_time: val }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>End Time</label>
                        <TimeSelect
                          value={newTariffSelection.end_time}
                          onChange={(val) => setNewTariffSelection(prev => ({ ...prev, end_time: val }))}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" className="btn btn-green" style={{ padding: '9px 14px' }} onClick={() => addTariffMatch(locIndex)}>
                          Add
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ padding: '9px 14px' }}
                          onClick={() => {
                            setAddingTariffFor(null);
                            setNewTariffSelection({ tariff_id: '', start_time: '', end_time: '', days_of_week: '', effective_start_date: '', effective_end_date: '' });
                            setNewShowDays(false);
                            setNewShowDates(false);
                          }}
                        >
                          Cancel
                        </button>
                      </div>

                      <div style={{ width: '100%', display: 'flex', gap: 20, marginTop: 4 }}>
                        <label className="advanced-toggle">
                          <input type="checkbox" checked={newShowDays} onChange={() => setNewShowDays(prev => !prev)} />
                          Specific days
                        </label>
                        <label className="advanced-toggle">
                          <input type="checkbox" checked={newShowDates} onChange={() => setNewShowDates(prev => !prev)} />
                          Specific date range
                        </label>
                      </div>

                      {newShowDays && (
                        <div style={{ width: '100%' }}>
                          <DayPicker
                            value={newTariffSelection.days_of_week}
                            onChange={(val) => setNewTariffSelection(prev => ({ ...prev, days_of_week: val }))}
                          />
                        </div>
                      )}

                      {newShowDates && (
                        <div className="form-grid" style={{ width: '100%', gridTemplateColumns: '1fr 1fr' }}>
                          <div className="form-group">
                            <label>Start Date</label>
                            <input
                              type="date"
                              value={newTariffSelection.effective_start_date || ''}
                              onChange={(e) => setNewTariffSelection(prev => ({ ...prev, effective_start_date: e.target.value }))}
                            />
                          </div>
                          <div className="form-group">
                            <label>End Date</label>
                            <input
                              type="date"
                              value={newTariffSelection.effective_end_date || ''}
                              onChange={(e) => setNewTariffSelection(prev => ({ ...prev, effective_end_date: e.target.value }))}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="add-entity-btn"
                      style={{ padding: 10, marginBottom: 0, marginTop: 4 }}
                      onClick={() => setAddingTariffFor(locIndex)}
                    >
                      <IconPlus /> Add Tariff
                    </button>
                  )}
                </div>

                <div className="bulk-field-grid">
                  <div className="bulk-field">
                    <label>Organisation Name *</label>
                    <input
                      type="text"
                      className={!loc.organisation_name ? 'field-error' : ''}
                      value={loc.organisation_name || ''}
                      onChange={(e) => updateOrgName(locIndex, e.target.value)}
                    />
                  </div>
                  {LOCATION_FIELD_NAMES.map(fieldName => {
                    const value = loc.location_fields[fieldName];
                    const isRequired = fieldName === 'station_name';
                    if (!value && !isRequired) return null;
                    return (
                      <EditableField
                        key={fieldName}
                        level="location"
                        fieldName={fieldName}
                        value={value}
                        isError={isRequired && !value}
                        onChange={(v) => updateLocationField(locIndex, fieldName, v)}
                      />
                    );
                  })}
                </div>

                {loc.chargers.map((charger, chargerIndex) => (
                  <div className="bulk-charger-block" key={chargerIndex}>
                    <div className="bulk-charger-header">
                      Charger {chargerIndex + 1}
                      {charger.charger_fields.ocpp_id && ` — ${charger.charger_fields.ocpp_id}`}
                    </div>

                    <div className="bulk-field-grid">
                      {CHARGER_FIELD_NAMES.map(fieldName => {
                        const value = charger.charger_fields[fieldName];
                        const isRequired = fieldName === 'ocpp_id';
                        if (!value && !isRequired) return null;
                        return (
                          <EditableField
                            key={fieldName}
                            level="charger"
                            fieldName={fieldName}
                            value={value}
                            isError={isRequired && !value}
                            onChange={(v) => updateChargerField(locIndex, chargerIndex, fieldName, v)}
                          />
                        );
                      })}
                    </div>

                    {charger.connectors.map((connector, connectorIndex) => (
                      <div className={`bulk-connector-block ${connector.valid ? '' : 'invalid'}`} key={connectorIndex}>
                        <div className="bulk-connector-header">
                          {connector.valid ? '✓' : '⚠'} Connector {connectorIndex + 1}
                          {!connector.valid && ` — ${connector.errors.join(', ')}`}
                        </div>
                        <div className="bulk-field-grid">
                          {CONNECTOR_FIELD_NAMES.map(fieldName => {
                            const value = connector.connector_fields[fieldName];
                            const isRequired = fieldName === 'connector_type' || fieldName === 'max_ampere';
                            if (!value && !isRequired) return null;
                            const isError = isRequired && !value;
                            return (
                              <EditableField
                                key={fieldName}
                                level="connector"
                                fieldName={fieldName}
                                value={value}
                                isError={isError}
                                onChange={(v) => updateConnectorField(locIndex, chargerIndex, connectorIndex, fieldName, v)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}